import { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import type { Contact } from '@taki/data-model';
import {
  Button,
  IconContact,
  Input,
  Modal,
  ScreenLayout,
  ViewHost,
  usePendingAppAction,
  usePref,
  useScreen,
} from '@taki/ui';
import type {
  ScreenDoor,
  ScreenFilterDef,
  ScreenSortOption,
  ScreenStat,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  CONTACT_APP_ID,
  CONTACT_SCREEN_ID,
  CONTACT_VIEW_TYPES,
  createContactBindings,
} from './views.js';
import {
  DEFAULT_FILTERS,
  DEFAULT_STATS,
  FILTER_DEFS,
  FILTERS_PREF_KEY,
  STAT_DEFS,
  STATS_MAX,
  STATS_PREF_KEY,
  type ContactFilterId,
  type ContactStatId,
} from './settings-defs.js';

/**
 * Ecran Contacts de l'app `contact`, tranche verticale de validation de l'archi
 * et MODELE DE REFERENCE du gabarit d'ecran standard (gabarit v2) ET des VUES
 * NATIVES du core.
 *
 * LE CONTRAT v2, EN UNE PHRASE : l'ecran declare une PORTE (un jeu de donnees,
 * zone 1) qui apporte ses filtres, ses actions, ses stats et son placeholder de
 * recherche ; le moteur (`useScreen`) tient l'etat, le chassis (`ScreenLayout`)
 * rend l'habit. Contact est l'app PAUVRE du patron : UNE seule porte, celle que
 * toute nouvelle app copie avant d'en avoir plusieurs.
 *
 * Ce que la porte remplace, venu de la V1 :
 * - les PASTILLES de filtre de l'ancienne zone 4 sont devenues le CATALOGUE de
 *   la porte (`filters`) : multi-choix additif dans le popover de l'outil
 *   filtre (3b), un filtre actif = un tag retirable dans le tiroir (4a). La
 *   pseudo-entree "Tous" a disparu : l'etat de repos montre tout, l'entree de
 *   sortie du popover ("Tout afficher") appartient au chassis ;
 * - l'action d'ajout est declaree `kind: 'add'` : le chassis la rend lui-meme
 *   en PREMIER, en lime, libelle universel "+ Ajouter" (regle fondateur du
 *   04/08/2026), jamais "Nouveau contact" ;
 * - le placeholder de recherche appartient a la porte
 *   (`screen.door.searchPlaceholder`), les stats aussi (`screen.door.stats`,
 *   calculees sur le jeu VIVANT de la porte, AVANT recherche et filtres :
 *   doctrine "stats insensibles au filtrage") ;
 * - le TRI est une pile multi-niveaux (popover de l'outil tri, 3b) : le
 *   catalogue `sortOptions` est une declaration d'ecran, il couvre TOUTES les
 *   cles triables (roles et colonnes), sinon un tri pose depuis l'en-tete du
 *   tableau rendrait un tag au libelle brut ;
 * - les FAVORIS (4b/4c) viennent du moteur : `tools={screen}` suffit, l'ecran
 *   ne cable rien.
 *
 * Structure imposee par la cage IA (voir docs/GABARIT-ECRAN.md) :
 * - le squelette de l'ecran vient de `ScreenLayout` (@taki/ui), jamais
 *   reconstruit a la main : rail (1), en-tete (2a titre, 2b recherche, 2c
 *   stats), commandes (3a actions, 3b outils, 3c vues), tiroir (4), contenu (5) ;
 * - acces data UNIQUEMENT via `@taki/sdk` (recupere par `useTakiApp`) : aucune
 *   vue ne recoit jamais le SDK, elle ne recoit que des props ;
 * - etats standard (chargement, erreur, vide, fiche introuvable) delegues au
 *   `ViewHost` : l'ecran ne passe donc NI `loading`, NI `error`, NI `empty` au
 *   gabarit, sinon la priorite serait tenue a deux endroits ;
 * - le formulaire d'ajout s'ouvre dans le composant `Modal` de @taki/ui,
 *   jamais dans la zone de contenu (regle G6) ;
 * - les reglages de l'app vivent dans `settings.tsx`, monte par le hub
 *   Parametres du shell (route /settings) via `loadSettings` du registre :
 *   l'ecran LIT les prefs partagees ("app.contact.filters" et
 *   "app.contact.stats") via `usePref` et se resynchronise en direct ;
 * - le shortcut "Nouveau contact" du dock (action `new` de la fiche
 *   taki.app.json) arrive via `usePendingAppAction` : l'ecran ouvre le meme
 *   Modal de creation puis marque l'action traitee avec `consume()`.
 *
 * La recherche, les filtres et le tri sont appliques COTE CLIENT par le core
 * (perimetre du lot 1 : le SDK ne pagine pas encore) ; le SDK cadre deja la
 * requete par orgId.
 */

/** L'id de la porte unique. Il voyage dans le parametre `s` de l'adresse. */
const CONTACT_DOOR_ID = 'all';

/**
 * Ce que chaque filtre du catalogue garde. Le `match` est ce qui permet au
 * CORE de filtrer : il s'applique AVANT la projection, donc il vaut pour toutes
 * les vues d'un coup. Multi-choix ADDITIF : poser les deux filtres AGRANDIT la
 * selection (un OU, jamais un ET).
 */
const FILTER_MATCH: Record<ContactFilterId, (contact: Contact) => boolean> = {
  'with-email': (contact) => contact.email != null,
  'without-email': (contact) => contact.email == null,
};

/**
 * LE CATALOGUE DE TRI de l'ecran (popover de l'outil tri, 3b). C'est une
 * declaration d'ecran, pas un etat : le moteur ne le connait pas. Il couvre
 * TOUTES les cles triables de l'ecran (le role `title` et les colonnes
 * `sortable`), parce que la pile de tri est UNIQUE pour toutes les vues : un
 * tri pose par l'en-tete du tableau doit rendre un tag lisible ici aussi.
 */
const SORT_OPTIONS: ScreenSortOption[] = [
  { key: 'title', label: 'Nom' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Telephone' },
  { key: 'createdAt', label: 'Cree le' },
];

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

export function ContactsScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  // Prefs partagees avec settings.tsx (hub Parametres du shell). L'ecran LIT,
  // le hub EDITE ; l'event "taki:pref-change" resynchronise en direct.
  const [filterVisibility] = usePref<Record<string, boolean>>(
    FILTERS_PREF_KEY,
    DEFAULT_FILTERS,
  );
  const [statIds] = usePref<string[]>(STATS_PREF_KEY, DEFAULT_STATS);

  // Le catalogue reellement declare par la porte : les entrees visibles selon
  // la pref (cle absente = visible, seul un `false` explicite masque).
  const filterDefs = useMemo<ScreenFilterDef<Contact>[]>(
    () =>
      FILTER_DEFS.filter((def) => filterVisibility[def.id] !== false).map((def) => ({
        id: def.id,
        label: def.label,
        match: FILTER_MATCH[def.id],
      })),
    [filterVisibility],
  );

  /* Fabriques UNE fois : le SDK est stable pour toute la vie de l'ecran, donc
     ces bindings le sont aussi. Les recreer a chaque rendu relancerait la
     projection a chaque frappe dans la recherche. */
  const bindings = useMemo(() => createContactBindings(sdk), [sdk]);

  // Formulaire de creation : il vit dans un Modal au-dessus de l'ecran (G6),
  // jamais dans la zone 5. Un seul modal par ecran. Declare AVANT la porte :
  // l'action d'ajout de la porte l'ouvre.
  const [formOpen, setFormOpen] = useState(false);

  /*
   * LA PORTE UNIQUE de l'ecran (zone 1). Elle apporte son catalogue de
   * filtres, ses actions (3a), ses stats (2c) et son placeholder de recherche
   * (2b) : l'ecran les repasse au gabarit via `screen.door.*`, sans rien
   * recalculer. Le tableau est recompose a chaque rendu (le moteur le lit par
   * ref) : les closures y restent fraiches, et les stats en forme fonction
   * recoivent le jeu VIVANT de la porte (archives exclues, avant recherche et
   * filtres) a chaque rendu.
   *
   * Les actions sont des DECLARATIONS, sans etat de chargement : "+ Ajouter"
   * reste actif pendant un chargement (le Modal flotte au-dessus de tous les
   * etats de la zone 5). Pas d'action Rafraichir : le rechargement appartient
   * au socle (G19), une action d'app AGIT sur les donnees.
   */
  const doors: ScreenDoor<Contact>[] = [
    {
      id: CONTACT_DOOR_ID,
      label: 'Tous les contacts',
      filters: filterDefs,
      actions: [
        {
          id: 'new',
          // Libelle IGNORE par le chassis : une action `kind: 'add'` est
          // rendue "+ Ajouter" (universel), en premier, en lime, icone
          // IconPlus figee (G5).
          kind: 'add',
          label: 'Ajouter',
          onClick: () => setFormOpen(true),
        },
      ],
      /*
       * LES TROIS GESTES DE FICHE (chantier 2_0), declares sur LA PORTE : le
       * moteur en fait les entrees du menu "..." de la rangee 3c, avec leurs
       * fenetres de confirmation, la fermeture de la fiche et le rechargement.
       * L'app ne fait qu'ECRIRE, et uniquement via le SDK.
       *
       * Une porte qui ne les declare PAS n'en perd pas les boutons : ils sont
       * rendus GRISES avec leur raison (G16). On les declare ici parce qu'un
       * contact s'archive et se supprime comme n'importe quelle fiche, et
       * parce que cet ecran est le modele que les autres apps copient.
       *
       * `remove` est la suppression du socle (trois etats : `archivedAt` +
       * `deletedAt`), jamais un delete dur : le SDK ne l'expose meme pas.
       */
      archive: async (contact: Contact) => {
        await sdk.contacts.archive(contact.id);
      },
      unarchive: async (contact: Contact) => {
        await sdk.contacts.unarchive(contact.id);
      },
      remove: async (contact: Contact) => {
        await sdk.contacts.remove(contact.id);
      },
      // Stats de la porte : la pref choisit LESQUELLES afficher (3 max, ordre
      // du catalogue), les valeurs restent calculees des donnees reelles.
      stats: (live: Contact[]): ScreenStat[] => {
        const withEmail = live.filter((contact) => contact.email != null).length;
        const withPhone = live.filter((contact) => contact.phone != null).length;
        const values: Record<ContactStatId, string> = {
          total: String(live.length),
          'with-email': String(withEmail),
          'without-email': String(live.length - withEmail),
          'with-phone': String(withPhone),
        };
        return STAT_DEFS.filter((def) => statIds.includes(def.id))
          .slice(0, STATS_MAX)
          .map((def) => ({ value: values[def.id], label: def.label }));
      },
      searchPlaceholder: 'Rechercher un contact ou demander a Lia...',
    },
  ];

  /*
   * L'ETAT D'ECRAN, tenu par le core.
   *
   * `useScreen` est appele ICI, visible dans le fichier d'ecran, jamais cache
   * dans un contexte : quand une vue se comporte mal, on lit l'etat la ou il
   * est cree. Il possede la requete (recherche, filtres, pile de tri, fenetre),
   * la vue active, l'element ouvert et son curseur gele, le tiroir et les
   * favoris. L'app, elle, ne possede que l'appel de donnees, les bindings et
   * la declaration de sa porte.
   */
  const screen = useScreen<Contact>({
    appId: CONTACT_APP_ID,
    screenId: CONTACT_SCREEN_ID,
    /*
     * Acces data UNIQUEMENT via le SDK, deja cadre par orgId.
     *
     * LE MODE ARCHIVES voyage dans la requete (`query.archivesMode`) et c'est
     * l'ECRAN qui le traduit en option de SDK. On relaie `'all'` et non
     * `'only'`, et ce n'est pas un detail : cet ecran est en pagination
     * CLIENTE (le chargeur rend tout), et ses stats (2c) comme son compteur de
     * porte (1b) se calculent sur ce qu'il a charge. Avec `'only'`, les
     * contacts actifs quitteraient la memoire et TOUS ces chiffres tomberaient
     * a zero le temps du mode : exactement ce que la regle G15 interdit (les
     * compteurs portent sur la porte BRUTE, donc sur les actifs). Avec
     * `'all'`, le moteur fait le partage tout seul : les archives a l'ecran,
     * les actifs dans les compteurs.
     *
     * Ce qu'on n'appelle JAMAIS : `count({ archived: 'only' | 'all' })`. Cette
     * agregation ne lit aucun document, donc elle ne peut pas retirer les
     * SUPPRIMES que le SDK retire cote client : le total depasserait la liste
     * (raison complete en tete de `packages/sdk/src/list-query.ts`).
     */
    load: (query) => sdk.contacts.list({ archived: query.archivesMode ? 'all' : 'exclude' }),
    getId: (contact) => contact.id,
    // Entree a froid (reprise de session sur une fiche) : on charge le contact
    // seul plutot que toute la collection.
    get: (id) => sdk.contacts.get(id),
    types: CONTACT_VIEW_TYPES,
    bindings,
    doors,
    defaultView: 'core.liste',
  });

  const { markRead, reload, removeFilter } = screen;
  const activeFilterIds = screen.activeFilterIds;

  // Shortcut du dock : le shell pose l'action en attente, l'ecran la consomme
  // dans l'effet plus bas (actionId non null seulement si elle vise cette app).
  const { actionId: pendingActionId, consume: consumePendingAction } =
    usePendingAppAction('contact');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Action posee par le dock (action "new" de la fiche taki.app.json) : ouvre
  // le formulaire de creation puis marque l'action traitee. `consume()` remet
  // l'action a null cote shell, l'effet ne rejoue donc pas (pas de double
  // ouverture).
  useEffect(() => {
    if (pendingActionId == null) return;
    if (pendingActionId === 'new') setFormOpen(true);
    consumePendingAction();
  }, [pendingActionId, consumePendingAction]);

  // Si un filtre ACTIF vient d'etre masque dans les reglages, il est RETIRE
  // (jamais de tag actif que le catalogue ne propose plus). Les autres filtres
  // poses survivent : on retire l'entree disparue, pas l'etat entier.
  useEffect(() => {
    for (const id of activeFilterIds) {
      if (filterVisibility[id] === false) removeFilter(id);
    }
  }, [activeFilterIds, filterVisibility, removeFilter]);

  // Fermeture du modal = remise a zero des champs (pas de brouillon conserve).
  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCreateError(null);
  }, []);

  const onCreate = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setCreateError(null);
      setCreating(true);
      try {
        // Acces data UNIQUEMENT via le SDK : les champs standard (id, dates,
        // schemaVersion, ...) sont remplis automatiquement par sdk.contacts.create.
        // Le modal ne cree que des PERSONNES : la creation d'une organisation
        // (raison sociale) arrive avec le volet app du chantier contact-type.
        const created = await sdk.contacts.create({
          type: 'person',
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          orgId: config.orgId,
        });
        // Modele de la boite mail : un message qu'on a envoye soi-meme ne
        // s'affiche pas comme non lu. On marque donc la fiche AVANT le
        // rechargement, sinon elle revient du serveur avec son lisere et sa
        // pastille, et il faudrait l'ouvrir pour eteindre ce qu'on vient
        // d'ecrire soi-meme.
        markRead(created.id);
        // Les donnees appartiennent au core : on lui demande de recharger
        // plutot que de tenir une copie locale. La liste reste visible pendant
        // le rechargement (l'etat chargement est reserve au PREMIER chargement).
        reload();
        // Creation reussie : le modal se ferme et les champs sont remis a zero.
        closeForm();
      } catch (err) {
        setCreateError(
          err instanceof Error ? err.message : 'Echec de la creation du contact.',
        );
      } finally {
        setCreating(false);
      }
    },
    [sdk, config.orgId, firstName, lastName, email, phone, closeForm, markRead, reload],
  );

  // Le compteur de la porte (zone 1b) : le jeu COMPLET, archives exclues. Il
  // est GELE par doctrine : insensible a la recherche, aux filtres ET au mode
  // Archives (G15). C'est ce filtre-ci qui le tient quand le mode est allume :
  // le chargeur ramene alors les deux etats, et seuls les actifs comptent.
  // Un compteur qui change de sens selon un mode ne veut plus rien dire.
  const liveContacts = useMemo(
    () => screen.records.filter((contact) => contact.archivedAt == null),
    [screen.records],
  );

  return (
    <>
      <ScreenLayout
        /* Zone 1 : identite de l'app (1a) et menu des portes (1b). Le rail
           s'arrete au menu : plus d'actions ni de zone meta dedans. */
        appName="Contact"
        appIcon={<IconContact size={18} />}
        menuItems={[
          { id: CONTACT_DOOR_ID, label: 'Tous les contacts', count: liveContacts.length },
        ]}
        activeMenuId={screen.section ?? CONTACT_DOOR_ID}
        onMenuSelect={screen.setSection}
        /* Zone 2a : le h1 affiche le label de la porte active ("Tous les
           contacts") ; `title` reste le repli. */
        title="Contacts"
        /* Zone 2c : les stats de la porte, resolues par le moteur (pref lue
           dans la declaration de la porte, valeurs sur le jeu vivant). */
        headerStats={screen.door.stats}
        /* Zone 2b : la recherche, centree sur l'ecran, placeholder de la
           porte. Le core filtre, pas l'ecran. */
        onSearchChange={screen.setSearch}
        searchValue={screen.query.search}
        searchPlaceholder={screen.door.searchPlaceholder}
        /* Zone 3a : les actions de la porte (regle "+ Ajouter" tenue par le
           chassis) puis celles que la vue active propose, deja forcees en
           secondaire par le moteur. */
        quickActions={screen.door.actions}
        viewActions={screen.viewActions}
        /* Zones 3b + 4 : outils (filtre, tri, etoile), tags et favoris. L'etat
           du moteur se passe TEL QUEL ; le catalogue de tri, lui, est une
           declaration d'ecran. */
        tools={screen}
        sortOptions={SORT_OPTIONS}
        /* Zone 3c : le selecteur de vues, epingle, JAMAIS conditionnel. Kanban
           et calendrier y sont GRISES avec leur raison (contacts sans champ de
           regroupement ni date metier), jamais absents. */
        views={screen.views}
        activeViewId={screen.viewId}
        onViewSelect={screen.setView}
        /* Rangee 3 au niveau FICHE : la puce de sortie (qui porte aussi
           `Echap`), l'indicateur d'enregistrement, et le curseur de fiche qui
           prend la place du selecteur a droite. Passees SANS condition : le
           gabarit ne les rend qu'au niveau `record`, et une fiche sans elles
           serait une nasse (aucune sortie, aucun retour sur l'ecriture, aucun
           voisin). */
        onRecordExit={() => screen.openRecord(null)}
        recordExitLabel={screen.views.find((view) => view.id === screen.lastCollectionViewId)?.label}
        saveState={screen.saveState}
        onRetrySave={screen.retrySave}
        recordCursor={screen.cursor}
        onRecordNavigate={screen.openRecord}
        /* Le menu "..." du groupe epingle (3c) : Archiver / Desarchiver et
           Supprimer. RIEN A CABLER ICI : `tools={screen}` plus haut les porte
           deja (`recordMenuActions`). Les brancher a la main les ferait
           dependre de l'app, alors que le chantier 2_0 les veut partout, sur
           toutes les portes, sans qu'aucune app n'ecrive une ligne. */
        /* La zone 5 est PRETEE au ViewHost (memoire de defilement par vue) et
           aux vues (mesure de cadre). */
        scrollRef={screen.scrollRef}
      >
        {/* Zone 5 pilotee par une VUE. Le ViewHost monte la vue active, tient
            les etats standard dans la MEME priorite que le gabarit (erreur >
            chargement > fiche introuvable > vide > contenu), la memoire de
            defilement par vue et le filet anti-plantage. L'etat chargement est
            le SKELETON de la vue active, temporise (pas de flash, pas de
            clignotement). L'etat vide ne depend JAMAIS du formulaire : le
            modal flotte au-dessus. */}
        <ViewHost
          state={screen}
          errorTitle="Echec du chargement"
          retryLabel="Reessayer"
          emptyTitle="Aucun contact pour l'instant"
          emptyHint='Clique sur "Ajouter" pour creer le premier.'
          notFoundTitle="Contact introuvable"
          notFoundHint="Ce contact a ete supprime, ou tu n'y as pas acces."
        />
      </ScreenLayout>

      {/* Formulaire de creation : pop-up centrale standard (G6), FRERE du
          ScreenLayout pour flotter au-dessus de tous les etats de la zone 5
          (vide compris). Champs poses a plat, actions dans le footer. */}
      <Modal
        open={formOpen}
        onClose={closeForm}
        title="Nouveau contact"
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={creating}>
              Annuler
            </Button>
            {/* Soumission du formulaire du corps via l'attribut natif `form`
                (le footer du Modal vit hors de l'element form). loading={creating}
                pendant l'ecriture SDK : le Button rend un spinner inline par-dessus
                le libelle (garde invisible, largeur stable), se desactive et pose
                aria-busy. Libelle fixe, pas de swap de texte : la largeur ne saute
                pas et l'ancien indicateur maison "Creation en cours" disparait. */}
            <Button
              variant="primary"
              type="submit"
              form="contact-create-form"
              loading={creating}
            >
              Creer le contact
            </Button>
          </>
        }
      >
        <form
          id="contact-create-form"
          onSubmit={onCreate}
          className="flex flex-col gap-3"
        >
          <Input
            aria-label="Prenom"
            placeholder="Prenom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <Input
            aria-label="Nom"
            placeholder="Nom"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <Input
            aria-label="Email"
            type="email"
            placeholder="Email (optionnel)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            aria-label="Telephone"
            placeholder="Telephone (optionnel)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {createError ? (
            <p className="text-sm text-danger" role="alert">
              {createError}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}

export default ContactsScreen;
